<template>
    <div class="step" :class="{ 'is-embedded': embedded }">
        <div class="column">
            <StepHeader v-if="embedded" embedded :title="t('onboarding.calendarFirst.assessmentStep.title')" :lede="t('onboarding.calendarFirst.assessmentStep.lede')" />
            <BasePageHeader
                v-else
                :title="t('onboarding.calendarFirst.assessmentStep.heading')"
                :subtitle="t('onboarding.calendarFirst.assessmentStep.lede')"
                no-border
                subtitle-below>
                <template #eyebrow
                    ><p>{{ t("onboarding.calendarFirst.assessmentStep.title") }}</p></template
                >
            </BasePageHeader>

            <PerspectiveSurvey
                ref="survey"
                class="survey"
                layout="embedded"
                :questions="assessment.questions"
                :saved-responses="assessment.saved_responses"
                :session-id="assessment.session_id"
                :submit-label="t('onboarding.calendarFirst.assessmentStep.submit')"
                @submitted="emit('submitted')">
                <!-- Embedded setup already provides an exit through its persistent rail; the linear flow
                     needs this skip control to proceed without answering.
                     -->
                <template v-if="skippable ?? !embedded" #escape>
                    <BaseButton theme="tertiary" class="step-skip" :disabled="busy" @click="emit('skip')">{{ t("onboarding.calendarFirst.actions.skipForNow") }}</BaseButton>
                </template>
            </PerspectiveSurvey>
        </div>
    </div>
</template>

<script setup lang="ts">
import { fetchApi } from "~vue/api";
import StepHeader from "~vue/CalendarFirstOnboarding/StepHeader.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import PerspectiveSurvey from "~vue/Perspective/PerspectiveSurvey.vue";
import { onboardingDevToolsStore, setAssessmentFillAvailable } from "~vue/stores/onboardingDevToolsStore";
import { computed, onBeforeUnmount, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { AssessmentState } from "./types";

const { embedded = false, skippable = undefined, assessment, busy } = defineProps<{ embedded?: boolean; skippable?: boolean; assessment: AssessmentState; busy: boolean }>();

const emit = defineEmits<{ submitted: []; skip: [] }>();
const { t } = useI18n();

const survey = useTemplateRef<InstanceType<typeof PerspectiveSurvey>>("survey");

/*
 * Staff testing bypasses sixty manual answers through the Perspectives demo endpoint, which persists them
 * before submission.
 */
watch(
    () => onboardingDevToolsStore.fillAssessmentToken,
    async (token) => {
        if (token === 0) {
            return;
        }
        const response = await fetchApi(`/v1/perspectives/sessions/${assessment.session_id}/fill-remaining`, { method: "POST" });
        if (!response.ok) {
            return;
        }
        const payload = (await response.json()) as { filled_responses?: Record<string, number> };
        survey.value?.adoptResponses(payload.filled_responses ?? {});
    },
);

const fillAvailable = computed(() => assessment.questions.length > 0);
watch(fillAvailable, (available) => setAssessmentFillAvailable(available), { immediate: true });

onBeforeUnmount(() => setAssessmentFillAvailable(false));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .step {
        @apply px-6 py-6 md:py-10;
    }
    .step.is-embedded {
        @apply p-0;
    }

    .column {
        @apply mx-auto w-full max-w-168;
    }
    .step.is-embedded .column {
        @apply mx-0 max-w-none;
    }

    .survey {
        @apply mt-6;
    }

    /* The host already sets the page gutter, and the survey's own would indent it
       against the heading directly above it. */
    .survey :deep(.progress),
    .survey :deep(.footer) {
        @apply px-0;
    }
    .survey :deep(.scroll-container) > * {
        @apply px-0;
    }
}
</style>
